/**
 * ⚠️ LEGACY — superseded by src/features/dashboards/api/queries.ts (Wave 3, 2026-05-18).
 *
 * `fetchAdminDashboard` and its slice still feed the legacy
 * `pages/Dashboard/AdminModernDashboard.tsx` page until routes are swapped.
 * Remove from src/slices/index.ts only after the legacy page is unrouted.
 *
 * DO NOT add features or fixes here. Use TanStack Query
 * (`useAdminDashboard`) from features/dashboards/api/ instead.
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as dashboardService from "../../services/dashboardService";
import type { AdminDashboardResponse } from "../../services/dashboardService";

interface DashboardState {
  data: AdminDashboardResponse | null;
  loading: boolean;
  error: string | null;
  lastFetched: string | null; // ISO date-time string
}

const initialState: DashboardState = {
  data: null,
  loading: false,
  error: null,
  lastFetched: null,
};

/**
 * Fetch Admin Dashboard data
 * @param limit - Optional limit for recent items (default: 5 for work orders/quotes, 4 for meetings)
 * 
 * Note: Upcoming meetings are filtered to show top 4 from today onwards (no date range limit)
 */
export const fetchAdminDashboard = createAsyncThunk(
  "dashboard/fetchAdminDashboard",
  async ({
    limit,
  }: {
    limit?: number;
  } = {}) => {
    const response: AdminDashboardResponse = await dashboardService.getAdminDashboard(
      limit
    );
    return response;
  }
);

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    clearDashboard(state) {
      state.data = null;
      state.error = null;
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Admin Dashboard
      .addCase(fetchAdminDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.lastFetched = action.payload.lastUpdated || new Date().toISOString();
        state.error = null;
      })
      .addCase(fetchAdminDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch dashboard data";
      });
  },
});

export const { clearError, clearDashboard } = dashboardSlice.actions;

// Selectors
export const selectDashboardData = (state: { dashboard: DashboardState }) =>
  state.dashboard.data;
export const selectDashboardLoading = (state: { dashboard: DashboardState }) =>
  state.dashboard.loading;
export const selectDashboardError = (state: { dashboard: DashboardState }) =>
  state.dashboard.error;
export const selectDashboardLastFetched = (state: { dashboard: DashboardState }) =>
  state.dashboard.lastFetched;

export default dashboardSlice.reducer;

